import { useState } from 'react';
import { useOutletContext, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { useAuth, IS_DEMO_MODE } from '@/lib/auth';
import { supabasePublic } from '@/lib/supabase';
import { Card, CardHeader, Button } from '@/components/ui';
import {
  voiceProfileSchema,
  VOICE_PROFILE_QUESTIONS,
  EMPTY_VOICE_PROFILE,
  type VoiceProfile,
} from '@/lib/voice-profile';
import { cn } from '@/lib/cn';
import type { Tenant } from '@/hooks/useTenant';

type Errors = Partial<Record<keyof VoiceProfile, string>>;

export function VoiceProfilePage() {
  const { tenant } = useOutletContext<{ tenant: Tenant }>();
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [values, setValues] = useState<VoiceProfile>(EMPTY_VOICE_PROFILE);
  const [errors, setErrors] = useState<Errors>({});

  const submitMutation = useMutation({
    mutationFn: async (payload: VoiceProfile) => {
      if (IS_DEMO_MODE) {
        await new Promise((r) => setTimeout(r, 400));
        return { id: 'demo-voice-profile' };
      }
      const supa = supabasePublic(() => getToken());
      const now = new Date().toISOString();
      const { data, error } = await supa
        .from('voice_profiles')
        .upsert(
          {
            client_id: tenant.id,
            payload,
            completed_at: now,
            updated_at: now,
          },
          { onConflict: 'client_id' }
        )
        .select('id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
      setTimeout(() => navigate('/'), 1500);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = voiceProfileSchema.safeParse(values);
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof VoiceProfile;
        if (!next[k]) next[k] = issue.message;
      }
      setErrors(next);
      return;
    }
    setErrors({});
    submitMutation.mutate(parsed.data);
  }

  function update<K extends keyof VoiceProfile>(field: K, value: VoiceProfile[K]) {
    setValues((v) => ({ ...v, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  }

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ducens-muted hover:text-ducens-ink">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </Link>

      <Card>
        <CardHeader
          title="Voice Profile"
          description="5 questions, ~10 minutes. Tes agents écriront comme toi (ou pas du tout)."
        />

        <form onSubmit={handleSubmit} aria-label="voice profile form" className="space-y-5" noValidate>
          {VOICE_PROFILE_QUESTIONS.map((q) => (
            <div key={q.field}>
              <label htmlFor={q.field} className="block text-sm font-medium mb-1.5">
                {q.label}
              </label>
              <textarea
                id={q.field}
                name={q.field}
                rows={4}
                placeholder={q.placeholder}
                value={values[q.field]}
                onChange={(e) => update(q.field, e.target.value)}
                aria-invalid={!!errors[q.field]}
                aria-describedby={errors[q.field] ? `${q.field}-error` : `${q.field}-hint`}
                className={cn(
                  'w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ducens-ink',
                  errors[q.field] ? 'border-ducens-danger' : 'border-ducens-border'
                )}
              />
              {errors[q.field] ? (
                <p id={`${q.field}-error`} className="text-xs text-ducens-danger mt-1">
                  {errors[q.field]}
                </p>
              ) : (
                <p id={`${q.field}-hint`} className="text-xs text-ducens-muted mt-1">
                  {q.hint}
                </p>
              )}
            </div>
          ))}

          {submitMutation.isError && (
            <div className="bg-red-50 border border-ducens-danger/40 rounded-md p-3 text-sm text-ducens-danger flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Impossible d'enregistrer. Réessaie ou écris à sebastian@ducens.io.
            </div>
          )}

          {submitMutation.isSuccess && (
            <div className="bg-green-50 border border-ducens-success/40 rounded-md p-3 text-sm text-ducens-success flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Voice profile enregistré. Redirection…
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" disabled={submitMutation.isPending}>
            {submitMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Enregistrer mon voice profile
          </Button>
        </form>
      </Card>
    </div>
  );
}
